import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const FAQ = [
  { q: "How does Peopels match me with a Helper?", a: "Our AI parses your task, picks the right category, then matches you with the closest available vetted Helper in seconds." },
  { q: "Is payment safe?", a: "Yes. Payment is held in escrow by Stripe and only released to your Helper after you confirm the job is complete." },
  { q: "Are Helpers vetted?", a: "Every Helper passes ID verification, address checks and onboarding before they can accept any job on Peopels." },
  { q: "What if something goes wrong?", a: "You can open a dispute from your booking — our UK support team reviews evidence and refunds where appropriate." },
];

export const Route = createFileRoute("/how-it-works")({
  head: () => ({
    meta: [
      { title: "How Peopels works — Hiring a Helper made simple" },
      { name: "description", content: "Post a task, get matched, pay securely. Here's exactly how Peopels works for customers and Helpers across Greater London." },
      { property: "og:title", content: "How Peopels works" },
      { property: "og:description", content: "Post a task, get matched, pay securely." },
      { property: "og:url", content: "/how-it-works" },
    ],
    links: [{ rel: "canonical", href: "/how-it-works" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to hire a local Helper on Peopels",
          step: [
            { "@type": "HowToStep", position: 1, name: "Describe what you need", text: "Type the task in plain English." },
            { "@type": "HowToStep", position: 2, name: "Pick a Helper", text: "Browse vetted, rated Helpers near you." },
            { "@type": "HowToStep", position: 3, name: "Book and chat", text: "Lock in a time and message securely in-app." },
            { "@type": "HowToStep", position: 4, name: "Get it done & rate", text: "Confirm completion and rate your Helper." },
          ],
        }),
      },
    ],
  }),
  component: Page,
});


function Page() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-4xl flex-1 px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="font-display text-4xl font-bold">How it works</h1>
        <ol className="mt-10 space-y-8">
          {[
            { t: "Describe what you need", d: "Type it in plain English — 'mount a 55-inch TV on plasterboard wall'. Our AI structures the job, suggests a fair price and finds the right category." },
            { t: "Pick a Helper", d: "Browse vetted, rated Helpers near you. Sort by distance, rating, response time or instant-book availability." },
            { t: "Book and chat", d: "Lock in a time, message securely in-app, share photos if needed. Payment is held safely until the job is done." },
            { t: "Get it done & rate", d: "Helper completes the job, you confirm and rate. Funds release. Repeat customers earn loyalty points and Helpers earn badges." },
          ].map((s, i) => (
            <li key={s.t} className="flex gap-5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary font-bold text-primary-foreground">{i + 1}</span>
              <div>
                <h2 className="text-xl font-semibold">{s.t}</h2>
                <p className="mt-1 text-muted-foreground">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </main>
      <Footer />
    </div>
  );
}
